import React, { useEffect, useState } from "react";

interface VideoData {
  id: number;
  title: string;
  videoUrl: string;
  thumbnailUrl: string;
  part: string;
}

const DEFAULT_VIDEO: VideoData = {
  id: 0,
  title: "Founder's Story featured here",
  videoUrl: "",
  thumbnailUrl: "",
  part: "Part 1",
};

const extractYouTubeId = (url: string) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace("www.", "");

    if (host === "youtube.com" || host === "m.youtube.com") {
      const videoId = parsed.searchParams.get("v");
      return videoId || "";
    }

    if (host === "youtu.be") {
      const videoId = parsed.pathname.replace("/", "");
      return videoId || "";
    }
  } catch (error) {
    console.error("Invalid YouTube URL:", error);
  }

  return "";
};

const buildYouTubeThumbnail = (url: string) => {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "";
};

const buildYouTubeEmbed = (url: string) => {
  const videoId = extractYouTubeId(url);
  return videoId
    ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`
    : "";
};

const getYouTubeWatchUrl = (url: string) => {
  const videoId = extractYouTubeId(url);
  if (videoId) {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
  return "";
};

const normalizeVideoData = (payload: any): VideoData => {
  if (!payload) {
    return DEFAULT_VIDEO;
  }

  const attributes = payload.attributes ?? payload;
  const videoUrl = attributes.videoUrl || DEFAULT_VIDEO.videoUrl;

  return {
    id: payload.id ?? DEFAULT_VIDEO.id,
    title: attributes.title || DEFAULT_VIDEO.title,
    videoUrl,
    thumbnailUrl:
      attributes.thumbnailUrl ||
      buildYouTubeThumbnail(videoUrl) ||
      DEFAULT_VIDEO.thumbnailUrl,
    part: attributes.part || DEFAULT_VIDEO.part,
  };
};

const WhereItAllBegan: React.FC = () => {
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/about-us-video?populate=*`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch video data");
        }

        const data = await response.json();
        setVideoData(normalizeVideoData(data?.data));
      } catch (error) {
        console.error("Error fetching video data:", error);
        setVideoData(DEFAULT_VIDEO);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoData();
  }, []);

  const handleWatchVideo = () => {
    if (videoData?.videoUrl) {
      window.open(getYouTubeWatchUrl(videoData.videoUrl) || videoData.videoUrl, "_blank");
    }
  };

  return (
    <div className="container mx-auto py-16 px-4">
      <div className="mb-16">
        <p className="text-navYellow text-xl font-medium mb-2">
          About Word Feast Ministry
        </p>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h1 className="text-3xl md:text-5xl font-bold text-darkBlue md:basis-1/2 md:max-w-[50%]">
            Where it all began
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl md:text-left md:basis-1/2 md:max-w-[50%] md:ml-auto">
            Empowering believers worldwide through the Consistent Bible Reading (CBR) Program
          </p>
        </div>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="w-full h-[500px] bg-gradient-to-br from-lightBlue to-purpleBlue rounded-2xl flex items-center justify-center">
            <div className="text-gray-500">Loading video...</div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl">
              {videoData?.videoUrl ? (
                <div className="aspect-video bg-black">
                  {buildYouTubeEmbed(videoData.videoUrl) ? (
                    <iframe
                      src={buildYouTubeEmbed(videoData.videoUrl)}
                      title={videoData.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="w-full h-full"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      Unable to load video
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-[500px] bg-gradient-to-br from-lightBlue to-purpleBlue flex items-center justify-center text-white">
                  Video unavailable
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhereItAllBegan;

